import { memoizeLruCache } from '@exodus/asset-lib'
import { ABI, isEthereumLikeAsset, isEthereumLikeToken, normalizeTxId } from '@exodus/ethereum-lib'
import SolidityContract from '@exodus/solidity-contract'
import assert from 'minimalistic-assert'

import * as ErrorWrapper from './error-wrapper.js'
import { getServer, getServerByName } from './exodus-eth-server/index.js'
import { fromHexToString } from './number-utils.js'

export const FORWARDER_CONTRACT_CODE =
  '0x5836818037808036817364b29dc43e817817cf77468c8dda63d98ce08fb25af43d91908282803e602b57fd5bf3'

// @Deprecated
export async function isContract(baseAssetName, address) {
  return getServerByName(baseAssetName).isContract(address)
}

export async function isContractAddress({ asset, address }) {
  return getServer(asset).isContract(address)
}

export const isContractAddressCached = memoizeLruCache(
  isContractAddress,
  ({ asset, address }) => `${asset.name}:${address}`,
  { max: 1000 }
)

export async function isForwarderContract({ asset, address }) {
  const server = getServer(asset)
  const contractCode = await server.getCode(address)
  return contractCode === FORWARDER_CONTRACT_CODE
}

export const isForwarderContractCached = memoizeLruCache(
  isForwarderContract,
  ({ asset, address }) => `${asset.name}:${address}`,
  { max: 1000 }
)

export async function getNonce({ asset, address, tag = 'latest' }) {
  const server = getServer(asset)
  const nonce = await ErrorWrapper.withErrorReason(
    server.getTransactionCount(address, tag),
    ErrorWrapper.reasons.nonceFetchFailed,
    'getNonce'
  )

  return parseInt(nonce, 16)
}

export async function estimateGas({ asset, ...args }) {
  return ErrorWrapper.withErrorReason(
    getServer(asset).estimateGas(args),
    ErrorWrapper.reasons.fetchGasLimitFailed,
    'estimateGas'
  )
}

// Only base assets, not tokens
export async function getBalance({ asset, address }) {
  if (!isEthereumLikeAsset(asset)) throw new Error(`unsupported asset ${asset.name}`)

  const server = getServer(asset)
  const balances = await server.getBalance(address)
  return balances?.confirmed?.value || '0'
}

// Only base assets, not tokens
export async function getBalanceProxied({ asset, address, tag = 'latest' }) {
  if (!isEthereumLikeAsset(asset)) throw new Error(`unsupported asset ${asset.name}`)

  const result = await getServer(asset).getBalanceProxied(address)
  return fromHexToString(result)
}

// Only ETH-like assets with token support
export async function getTokenBalance({ asset, address }) {
  if (!isEthereumLikeToken(asset)) throw new Error(`unsupported ETH-like token ${asset.name}`)

  const server = getServer(asset)
  const balances = await server.getBalance(address)
  const contractAddress = asset.contract.address.toLowerCase()
  return balances?.confirmed?.[contractAddress] || '0'
}

export async function getTokenBalanceFromNode({ asset, address }) {
  if (!isEthereumLikeToken(asset)) throw new Error(`unsupported ETH-like token ${asset.name}`)

  const server = getServer(asset)
  const contractAddress = asset.contract.address.toLowerCase()
  const balances = await server.balanceOf(address, contractAddress)
  return balances?.confirmed?.[contractAddress] || '0'
}

// Returns function for supplied asset
export function sendRawTransaction(asset) {
  return getServer(asset).sendRawTransaction
}

export async function transactionExists({ asset, txId }) {
  const server = getServer(asset)
  txId = normalizeTxId(txId)
  const txResult = await server.getTransactionByHash(txId)
  return Boolean(txResult && txResult.hash === txId)
}

export async function getTransaction({ asset, txId }) {
  const server = getServer(asset)
  txId = normalizeTxId(txId)
  return server.getTransactionByHash(txId)
}

export const getIsForwarderContract = memoizeLruCache(
  isForwarderContract,
  ({ asset, address }) => `${asset.name}:${address}`,
  { max: 100 }
)

const ERC20 = new SolidityContract(ABI.erc20)
const ERC20BytesParams = new SolidityContract(ABI.erc20BytesParams)
const DEFAULT_PARAM_NAMES = ['decimals', 'name', 'symbol']
const erc20ParamsCache = Object.create(null)

export const getERC20Params = async ({ asset, address, paramNames = DEFAULT_PARAM_NAMES } = {}) => {
  assert(asset, 'getERC20Params(): asset required')
  assert(address, 'getERC20Params(): address required')

  const cacheKey = `${address}:${paramNames}`
  if (erc20ParamsCache[cacheKey]) {
    return erc20ParamsCache[cacheKey]
  }

  const server = getServer(asset)

  const paramValues = await Promise.all(
    paramNames.map(async (method) => {
      let callResponse
      try {
        callResponse = await server.ethCall({ to: address, data: ERC20[method].methodId })
      } catch (err) {
        if (err.message === 'execution reverted') {
          throw new ErrorWrapper.EthLikeError({
            message: `Can't find parameters for contract with address ${address}. Are you sure it is a valid ERC20 contract?`,
            reason: ErrorWrapper.reasons.ethCallErc20Failed,
            hint: 'ethCall:executionReverted',
          })
        }

        throw new ErrorWrapper.EthLikeError({
          message: err.message,
          reason: ErrorWrapper.reasons.ethCallErc20Failed,
          hint: 'ethCall',
        })
      }

      if (method === 'decimals') return parseInt(callResponse)

      try {
        return ERC20.decodeOutput({ method, data: callResponse })[0]
      } catch (err) {
        // sometimes ERC20s violate the standard and use 'bytes32' type instead of 'string'
        if (err.message.includes('overflow') && callResponse) {
          const hex = ERC20BytesParams.decodeOutput({ method, data: callResponse })[0]
          const rawName = Buffer.from(hex.split('0x')[1], 'hex').toString()

          // trims 'Maker\x00\x00\x00...' to 'Maker'
          return rawName.slice(0, rawName.indexOf('\x00'))
        }
      }
    })
  )

  const response = Object.fromEntries(
    paramNames.map((paramName, index) => [paramName, paramValues[index]])
  )
  erc20ParamsCache[cacheKey] = response

  return response
}
